/**
 * Footsteps: a step on every foot plant (anim.ts `plant`, the run cycle's heel strikes) through
 * the audio engine, so they follow the sound switch and Combat volume. +audio: the sound follows
 * the floor under the foot (`surfaceAt`: stone, dirt, wood, carpet, grass, gravel, wet →
 * `step.<surface>`: 6-10 single footfalls each, sliced from TomMusic's footstep files; the synth
 * `step` recipe is the fallback). The local player's steps are soft but clear (a hair of left /
 * right pan and pitch per foot); everyone else's are quieter, fall off with distance from the
 * camera focus and are capped per frame. Each unit is rate-limited to its real step interval
 * (stride / speed), so a frame hitch or a gait flip never double-fires a foot. Plants are queued
 * during the unit update and played after the frame's combat sounds (the per-frame voice budget).
 */
import * as THREE from 'three';
import type { PlayOpts } from '../../../audio/engine';
import { strideLength } from '../gait';

/** What footsteps need from the audio engine. */
export interface StepAudio {
  readonly ready: boolean;
  play(id: string, opts?: PlayOpts): boolean;
}

/** Gain of the local player's steps / everyone else's (clips sit at the -30 LUFS `foot` role, under music and ambience). */
export const STEP_GAIN_MINE = 0.45;
export const STEP_GAIN_OTHER = 0.18;
/** A unit's next step may sound once this share of its expected step interval has passed. */
export const STEP_MIN_SHARE = 0.6;
/** Floor on the step interval (s): no unit steps faster than this, whatever its speed. */
export const STEP_MIN_S = 0.12;
/** Per-foot colour for the local player: left / right pitch and pan offsets (subtle). */
export const FOOT_PITCH: Readonly<Record<1 | 2, number>> = { 1: 1, 2: 0.985 };
export const FOOT_PAN: Readonly<Record<1 | 2, number>> = { 1: -0.06, 2: 0.06 };
/** Others' steps beyond this distance (yards) from the camera focus are silent. */
export const STEP_RANGE = 22;
/** Most other units' steps played per frame. */
const MAX_OTHERS = 1;
/** Above this ground speed (yd/s) a plant also kicks a tiny dust puff. */
export const DUST_SPEED = 6.5;

interface Queued {
  mine: boolean;
  x: number;
  y: number;
  z: number;
  foot: 1 | 2;
  speed: number;
}

const V = new THREE.Vector3();

/** Step gain for a plant: mine, or others attenuated by distance `d` from the focus (0 = silent). */
export function stepGain(mine: boolean, d: number, speed: number): number {
  // Faster runs land a touch heavier.
  const pace = 0.8 + 0.2 * Math.min(1, speed / 7);
  if (mine) return STEP_GAIN_MINE * pace;
  if (d > STEP_RANGE) return 0;
  return STEP_GAIN_OTHER * pace * (1 - d / STEP_RANGE);
}

/** Expected seconds between two steps (one foot to the other) of a unit `height` tall at `speed` yd/s. */
export function stepInterval(speed: number, height = 2): number {
  return speed > 0.01 ? Math.max(STEP_MIN_S, strideLength(height) / 2 / speed) : Infinity;
}

export class Footsteps {
  private readonly q: Queued[] = [];
  private n = 0;
  /** Last audible step per unit (s, `clock`), for the per-unit rate limit. */
  private readonly lastStep = new Map<string, number>();
  /** +audio: floor surface at a sim-plane point (null: stone). */
  surfaceAt: ((x: number, y: number) => string) | null = null;
  /** Seconds (tests override it). */
  clock: () => number = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;

  constructor(
    private readonly audio: StepAudio,
    /** Optional dust puff at a fast plant (world point; `mine` picks the fx priority). */
    private readonly dust?: (at: THREE.Vector3, mine: boolean) => void,
  ) {}

  /**
   * A foot planted at world point `at` (three.js space). Call during the unit update. `unit` /
   * `height` (optional) rate-limit that unit's steps to its real cadence. Returns whether the
   * step was queued for sound.
   */
  plant(mine: boolean, foot: 1 | 2, at: THREE.Vector3, speed: number, unit?: string, height = 2): boolean {
    if (speed > DUST_SPEED) this.dust?.(at, mine);
    if (!this.audio.ready || this.n >= 8) return false;
    if (unit !== undefined) {
      const now = this.clock();
      const last = this.lastStep.get(unit);
      if (last !== undefined && now - last < STEP_MIN_SHARE * stepInterval(speed, height)) return false;
      if (this.lastStep.size > 256) this.lastStep.clear();
      this.lastStep.set(unit, now);
    }
    const e = (this.q[this.n] ??= { mine: false, x: 0, y: 0, z: 0, foot: 1, speed: 0 });
    e.mine = mine;
    e.x = at.x;
    e.y = at.y;
    e.z = at.z;
    e.foot = foot;
    e.speed = speed;
    this.n++;
    return true;
  }

  /** Play the queued steps (after the frame's combat sounds). `focus`: sim-plane camera focus. */
  flush(camera: THREE.Camera, focus: { x: number; y: number }): void {
    let others = 0;
    for (let i = 0; i < this.n; i++) {
      const e = this.q[i]!;
      if (!e.mine && others >= MAX_OTHERS) continue;
      // three.js z = -sim y.
      const g = stepGain(e.mine, Math.hypot(e.x - focus.x, -e.z - focus.y), e.speed);
      if (g <= 0.01) continue;
      if (!e.mine) others++;
      V.set(e.x, e.y + 0.1, e.z).project(camera);
      const surface = this.surfaceAt?.(e.x, -e.z) ?? 'stone';
      const pan = V.x * 0.6 + (e.mine ? FOOT_PAN[e.foot] : 0);
      this.audio.play(`step.${surface}`, { gain: g, pan: Math.max(-1, Math.min(1, pan)), pitch: FOOT_PITCH[e.foot], prioBoost: e.mine ? 1 : 0 });
    }
    this.n = 0;
  }
}
